import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Crosshair } from "lucide-react";

interface FocalPointEditorProps {
  /** Full-size preview image URL (typically media.url). */
  src: string;
  alt?: string;
  /** Current focal point (0..1). */
  focalPointX: number;
  focalPointY: number;
  /** Fired live while dragging. */
  onChange: (focal: { focalPointX: number; focalPointY: number }) => void;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Draggable focal-point picker. A dot is placed over the full preview; dragging it
 * updates {focalPointX, focalPointY} in 0..1 live on every pointer move. The crop previews
 * (web surfaces incl. the 400×400 square) live next to it in `FocalSurfacePreviews`, which
 * follow the point as it is dragged. No extra npm dependency — pointer events on a
 * positioned overlay.
 */
export default function FocalPointEditor({
  src,
  alt = "",
  focalPointX,
  focalPointY,
  onChange,
}: FocalPointEditorProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const updateFromEvent = useCallback(
    (clientX: number, clientY: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = clamp01((clientX - rect.left) / rect.width);
      const y = clamp01((clientY - rect.top) / rect.height);
      onChange({
        focalPointX: Math.round(x * 1000) / 1000,
        focalPointY: Math.round(y * 1000) / 1000,
      });
    },
    [onChange],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => updateFromEvent(e.clientX, e.clientY);
    const onUp = () => setDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, updateFromEvent]);

  return (
    <div className="flex flex-col">
      {/* Full preview with draggable dot */}
      <div>
        <div
          ref={containerRef}
          onPointerDown={(e) => {
            e.preventDefault();
            setDragging(true);
            updateFromEvent(e.clientX, e.clientY);
          }}
          // Obal sedí přesně na vykreslenou fotku (w-fit + obrázek bez letterboxu), takže
          // obdélník z getBoundingClientRect je obdélník fotky a bod 0..1 i puntík odpovídají
          // souřadnicím fotky i u fotek na výšku (žádné prázdné pruhy po stranách).
          className="relative mx-auto w-fit max-w-full cursor-crosshair select-none overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <img
            src={src}
            alt={alt}
            className="block h-auto max-h-72 w-auto max-w-full"
            draggable={false}
          />
          {/* Crosshair lines */}
          <div
            className="pointer-events-none absolute inset-y-0 w-px bg-white/70 mix-blend-difference"
            style={{ left: `${focalPointX * 100}%` }}
          />
          <div
            className="pointer-events-none absolute inset-x-0 h-px bg-white/70 mix-blend-difference"
            style={{ top: `${focalPointY * 100}%` }}
          />
          {/* Dot */}
          <div
            className="bg-primary-500/80 pointer-events-none absolute -ml-3 -mt-3 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white shadow"
            // orez-vyjimka: pozice puntíku v editoru, ne hodnota ořezu
            style={{ left: `${focalPointX * 100}%`, top: `${focalPointY * 100}%` }}
          >
            <Crosshair className="h-3 w-3 text-white" />
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {t("media.focal.dragHint", { x: focalPointX.toFixed(2), y: focalPointY.toFixed(2) })}
        </p>
      </div>
    </div>
  );
}
